/**
 * Changing a view inside the cached list of them.
 *
 * A view's filters and sortings arrive nested inside the view, so there is no
 * cache entry of their own to write: `qk.views(tableId)` holds the whole list
 * and a change to one sort is a change to one view in it. These are the pure
 * functions that make that change, kept here so they can be tested against
 * plain arrays rather than through a mutation.
 *
 * Every one of them returns the array it was given when nothing matched, which
 * is what lets a caller write back only what actually changed -- the same rule
 * the realtime handlers follow for the application list.
 */
import { groupsWithin } from '../rows/rowMatch'
import type {
  View,
  ViewDecoration,
  ViewFilter,
  ViewFilterGroup,
  ViewGroupBy,
  ViewSort,
} from '@/api/types'

/** Replaces one view in the list, by id. */
export function patchView(
  views: readonly View[],
  viewId: number,
  change: (view: View) => View,
): View[] {
  return views.map((view) => (view.id === viewId ? change(view) : view))
}

/**
 * Adds a sort to a view, or replaces the one it already holds with that id.
 *
 * One function for creating and for updating, because the two answers differ
 * only in whether the id is already there -- and an update that appended
 * instead would leave the view sorted twice by the same field, which the
 * backend refuses and the client would then be alone in believing.
 */
export function withSort(views: readonly View[], viewId: number, sort: ViewSort): View[] {
  return patchView(views, viewId, (view) => {
    const sortings = view.sortings ?? []
    const held = sortings.some((candidate) => candidate.id === sort.id)
    return {
      ...view,
      sortings: held
        ? sortings.map((candidate) => (candidate.id === sort.id ? sort : candidate))
        : [...sortings, sort],
    }
  })
}

export function withoutSort(
  views: readonly View[],
  viewId: number,
  sortId: number,
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    sortings: (view.sortings ?? []).filter((sort) => sort.id !== sortId),
  }))
}

/** The same pair for filters, which hang off a view exactly as sortings do. */
export function withFilter(
  views: readonly View[],
  viewId: number,
  filter: ViewFilter,
): View[] {
  return patchView(views, viewId, (view) => {
    const filters = view.filters ?? []
    const held = filters.some((candidate) => candidate.id === filter.id)
    return {
      ...view,
      filters: held
        ? filters.map((candidate) => (candidate.id === filter.id ? filter : candidate))
        : [...filters, filter],
    }
  })
}

export function withoutFilter(
  views: readonly View[],
  viewId: number,
  filterId: number,
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    filters: (view.filters ?? []).filter((filter) => filter.id !== filterId),
  }))
}

/**
 * And again for group bys, which hang off a view exactly as sortings do.
 *
 * Written as its own pair rather than as one generic over the three, because
 * the key differs -- `sortings`, `filters`, `group_bys` -- and a function taking
 * the key as a string is a function nothing type-checks.
 */
export function withGroupBy(
  views: readonly View[],
  viewId: number,
  groupBy: ViewGroupBy,
): View[] {
  return patchView(views, viewId, (view) => {
    const groupBys = view.group_bys ?? []
    const held = groupBys.some((candidate) => candidate.id === groupBy.id)
    return {
      ...view,
      group_bys: held
        ? groupBys.map((candidate) => (candidate.id === groupBy.id ? groupBy : candidate))
        : [...groupBys, groupBy],
    }
  })
}

export function withoutGroupBy(
  views: readonly View[],
  viewId: number,
  groupById: number,
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    group_bys: (view.group_bys ?? []).filter((groupBy) => groupBy.id !== groupById),
  }))
}

/**
 * Adds a decoration to a view, or replaces the one it holds with that id.
 *
 * The same create-or-update pair as the filters and sortings, and it carries
 * more here than there: every edit to a colour rewrites the whole
 * `value_provider_conf`, so a change and a creation arrive as the same answer
 * and differ only in whether the id is already on the view.
 */
export function withDecoration(
  views: readonly View[],
  viewId: number,
  decoration: ViewDecoration,
): View[] {
  return patchView(views, viewId, (view) => {
    const decorations = view.decorations ?? []
    const held = decorations.some((candidate) => candidate.id === decoration.id)
    return {
      ...view,
      decorations: held
        ? decorations.map((candidate) =>
            candidate.id === decoration.id ? decoration : candidate,
          )
        : [...decorations, decoration],
    }
  })
}

export function withoutDecoration(
  views: readonly View[],
  viewId: number,
  decorationId: number,
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    decorations: (view.decorations ?? []).filter(
      (decoration) => decoration.id !== decorationId,
    ),
  }))
}

/**
 * The views of a table in a new order, renumbered as the server will have them.
 *
 * `order` rather than array position, because that is what the reader sorts by
 * -- `byOrder` in `shared/lib/order.ts` -- and an array reordered without it
 * would snap back the moment anything re-read the list. Numbered from one, as
 * the backend's own reordering does.
 *
 * A view the list does not name keeps the order it had. That is not tidiness:
 * a view created in another tab while this one was dragging is a view the
 * request never mentioned, and giving it a number here would be inventing a
 * position the server did not agree to.
 */
export function withViewOrder(
  views: readonly View[],
  viewIds: readonly number[],
): View[] {
  return views.map((view) => {
    const position = viewIds.indexOf(view.id)
    return position === -1 ? view : { ...view, order: position + 1 }
  })
}

/**
 * A view's sorts or group bys in a new priority order.
 *
 * Both the array and the `priority` on each term, because both are read: the
 * menu shows them in array order and the row query's signature sorts them by
 * priority. The backend's own ordering is `("priority", "id")`, so writing both
 * is writing exactly what a refetch would return -- and writing only one of
 * them would leave the menu and the rows disagreeing about which sort comes
 * first.
 */
function inPriorityOrder<T extends { id: number; priority: number }>(
  terms: readonly T[],
  ids: readonly number[],
): T[] {
  const named = ids.flatMap((id) => {
    const term = terms.find((candidate) => candidate.id === id)
    return term === undefined ? [] : [term]
  })
  // Anything the request did not name keeps its place after the rest, for the
  // reason a view left out of a reorder keeps its order: it arrived from
  // somewhere else and nothing here knows where the server will put it.
  const rest = terms.filter((term) => !ids.includes(term.id))
  return [...named, ...rest].map((term, index) => ({ ...term, priority: index }))
}

export function withSortOrder(
  views: readonly View[],
  viewId: number,
  sortIds: readonly number[],
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    sortings: inPriorityOrder(view.sortings ?? [], sortIds),
  }))
}

export function withGroupByOrder(
  views: readonly View[],
  viewId: number,
  groupByIds: readonly number[],
): View[] {
  return patchView(views, viewId, (view) => ({
    ...view,
    group_bys: inPriorityOrder(view.group_bys ?? [], groupByIds),
  }))
}

/**
 * Adds a filter group to a view, or replaces the one it holds with that id.
 *
 * The same create-or-update shape the filters and sortings use, for the same
 * reason: the two answers differ only in whether the id is already there.
 */
export function withFilterGroup(
  views: readonly View[],
  viewId: number,
  group: ViewFilterGroup,
): View[] {
  return patchView(views, viewId, (view) => {
    const groups = view.filter_groups ?? []
    const held = groups.some((candidate) => candidate.id === group.id)
    return {
      ...view,
      filter_groups: held
        ? groups.map((candidate) => (candidate.id === group.id ? group : candidate))
        : [...groups, group],
    }
  })
}

/**
 * Removes a filter group, everything nested inside it, and every filter in any
 * of them.
 *
 * The whole subtree, because that is what the backend deletes: a group is the
 * parent of its conditions, so removing one removes them. Leaving them in the
 * cache would leave conditions belonging to a group that is not there -- which
 * `buildFilterTree` attaches to the root rather than dropping, so they would
 * reappear at the top level as filters nobody wrote.
 */
export function withoutFilterGroup(
  views: readonly View[],
  viewId: number,
  groupId: number,
): View[] {
  return patchView(views, viewId, (view) => {
    const groups = view.filter_groups ?? []
    const removed = groupsWithin(groups, groupId)

    return {
      ...view,
      filter_groups: groups.filter((group) => !removed.has(group.id)),
      filters: (view.filters ?? []).filter(
        (filter) => filter.group == null || !removed.has(filter.group),
      ),
    }
  })
}
